import { useEffect, useMemo, useState } from "react";
import {
  calculateMaterialBalance,
  createMarkerCode,
  formatNumber,
  type MaterialMovement,
  type MaterialMovementType,
} from "./domain";
import { TimeTimeline } from "./TimeTimeline";
import {
  DEFAULT_TIME_SETTINGS,
  applySettingsToDraftDays,
  createTimeEntry,
  durationHours,
  durationLabel,
  entryHourTotal,
  formatClock,
  normalizeTimeEntry,
  parseClock,
  splitTimeEntry,
  type TimeEntry,
  type TimeSettings,
} from "./time-blocks";

type View = "home" | "plan" | "time" | "materials" | "resources" | "reports";
type EventType = "Tehty työ" | "Vaurio" | "Puute" | "Muistiinpano";
type ResourceCategory = "Työkalu" | "Kone" | "Mittalaite" | "Ajoneuvo";
type ResourceStatus = "Vapaa" | "Käytössä" | "Huollossa";

interface SiteEvent {
  id: string;
  type: EventType;
  description: string;
  floor: string;
  room: string;
  x: number;
  y: number;
  createdAt: string;
  billingImpact: boolean;
  photos: string[];
  status: "Luonnos" | "Lähetetty" | "Tarkistettu";
}

interface MaterialLot {
  id: string;
  code: string;
  name: string;
  unit: string;
  location: string;
  movements: MaterialMovement[];
}

interface ResourceItem {
  id: string;
  code: string;
  name: string;
  category: ResourceCategory;
  status: ResourceStatus;
  location: string;
  assignedTo?: string;
  inspectionDue?: string;
  notes?: string;
}

interface LappuData {
  events: SiteEvent[];
  timeEntries: TimeEntry[];
  timeSettings: TimeSettings;
  materialLots: MaterialLot[];
  resources: ResourceItem[];
  queue: number;
}

const FLOOR_NAMES = ["1. kerros", "2. kerros", "3. kerros"];
const DAYS = ["2026-07-20", "2026-07-21", "2026-07-22", "2026-07-23", "2026-07-24"];
const RESOURCE_LOCATIONS = [
  "1. krs · Työkaluvarasto",
  "1. krs · Varasto A",
  "2. krs · Tekninen tila 2.14",
  "3. krs · Asennusalue",
  "Piha · Huoltoalue",
  "Työmaa-auto",
];
const WORKERS = ["Mikko Mallikas", "Sari Sähkö", "Janne Johtaja"];
const DEFAULT_DATA: LappuData = {
  events: [
    {
      id: "event-demo-1",
      type: "Vaurio",
      description: "Kaapelihyllyn kiinnike vaurioitunut. Alue rajattu ja työnjohto informoitu.",
      floor: "2. kerros",
      room: "Tekninen tila 2.14",
      x: 68,
      y: 39,
      createdAt: "2026-07-24T08:42:00.000Z",
      billingImpact: true,
      photos: ["vaurio-yleiskuva.jpg", "kiinnike-lähikuva.jpg"],
      status: "Lähetetty",
    },
  ],
  timeEntries: DAYS.map((date, index) => ({
    ...createTimeEntry(date, index),
    description: index < 3 ? "Nousujohtojen asennus, B-rappu" : "",
    submitted: index < 3,
  })),
  timeSettings: DEFAULT_TIME_SETTINGS,
  materialLots: [
    {
      id: "lot-demo-1",
      code: "K7-P4M",
      name: "MCMK 4×16+16 kaapeli",
      unit: "m",
      location: "1. krs · Varasto A",
      movements: [
        { id: "move-1", type: "received", amount: 120, at: "2026-07-22T09:10:00.000Z" },
        { id: "move-2", type: "used", amount: 42, at: "2026-07-23T13:30:00.000Z" },
        { id: "move-3", type: "inventoried", amount: 76, at: "2026-07-24T07:50:00.000Z" },
      ],
    },
  ],
  resources: [
    {
      id: "resource-demo-1",
      code: "TY-014",
      name: "Akkuporakone 18 V",
      category: "Työkalu",
      status: "Käytössä",
      location: "2. krs · Tekninen tila",
      assignedTo: "Mikko Mallikas",
      notes: "2 akkua ja laturi",
    },
    {
      id: "resource-demo-2",
      code: "MI-006",
      name: "Asennustesteri Fluke 1664 FC",
      category: "Mittalaite",
      status: "Vapaa",
      location: "1. krs · Työkaluvarasto",
      inspectionDue: "2026-09-15",
    },
    {
      id: "resource-demo-3",
      code: "KO-003",
      name: "Saksinostin 8 m",
      category: "Kone",
      status: "Huollossa",
      location: "Piha · Huoltoalue",
      inspectionDue: "2026-07-28",
      notes: "Turvakaiteen lukitus tarkistettava",
    },
    {
      id: "resource-demo-4",
      code: "TY-021",
      name: "Kaapelivetosukka 20–30 mm",
      category: "Työkalu",
      status: "Vapaa",
      location: "1. krs · Työkaluvarasto",
    },
  ],
  queue: 2,
};

const NAV_ITEMS: Array<{ id: View; label: string; short: string }> = [
  { id: "home", label: "Etusivu", short: "E" },
  { id: "plan", label: "Pohjakuva", short: "P" },
  { id: "time", label: "Tunnit", short: "T" },
  { id: "materials", label: "Materiaalit", short: "M" },
  { id: "resources", label: "Resurssit", short: "K" },
  { id: "reports", label: "Raportit", short: "R" },
];

function usePersistentData() {
  const [data, setData] = useState<LappuData>(() => {
    try {
      const stored = localStorage.getItem("lappu-mvp-data");
      if (!stored) return DEFAULT_DATA;
      const parsed = JSON.parse(stored) as Partial<LappuData>;
      return {
        ...DEFAULT_DATA,
        ...parsed,
        events: parsed.events ?? DEFAULT_DATA.events,
        timeSettings: {
          ...DEFAULT_TIME_SETTINGS,
          ...(parsed.timeSettings ?? {}),
        },
        timeEntries: (parsed.timeEntries ?? DEFAULT_DATA.timeEntries).map((entry) =>
          normalizeTimeEntry(entry, {
            ...DEFAULT_TIME_SETTINGS,
            ...(parsed.timeSettings ?? {}),
          })),
        materialLots: parsed.materialLots ?? DEFAULT_DATA.materialLots,
        resources: parsed.resources ?? DEFAULT_DATA.resources,
      };
    } catch {
      return DEFAULT_DATA;
    }
  });

  useEffect(() => {
    localStorage.setItem("lappu-mvp-data", JSON.stringify(data));
  }, [data]);

  return [data, setData] as const;
}

function App() {
  const [view, setView] = useState<View>("home");
  const [data, setData] = usePersistentData();
  const [role, setRole] = useState<"Työntekijä" | "Työnjohtaja">("Työntekijä");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [printMode, setPrintMode] = useState<"timesheet" | "journal" | null>(null);
  const [toast, setToast] = useState("");

  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!printMode) return;
    const timer = window.setTimeout(() => window.print(), 80);
    const reset = () => setPrintMode(null);
    window.addEventListener("afterprint", reset, { once: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", reset);
    };
  }, [printMode]);

  const queueChange = (amount = 1) =>
    setData((current) => ({ ...current, queue: Math.max(0, current.queue + amount) }));

  const syncNow = () => {
    if (!isOnline) {
      setToast("Ei verkkoyhteyttä – kirjaukset säilyvät jonossa.");
      return;
    }
    setData((current) => ({ ...current, queue: 0 }));
    setToast("Kaikki kirjaukset synkronoitu.");
  };

  return (
    <>
      <div className={`app-shell view-${view}`}>
        <aside className="sidebar" aria-label="Päänavigaatio">
          <button className="brand" onClick={() => setView("home")} aria-label="Lappu etusivulle">
            <span className="brand-mark">L</span>
            <span>
              <strong>Lappu</strong>
              <small>työmaapäiväkirja</small>
            </span>
          </button>

          <nav className="nav-list">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                className={view === item.id ? "nav-item active" : "nav-item"}
                onClick={() => setView(item.id)}
              >
                <span className="nav-icon" aria-hidden="true">{item.short}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="demo-note">
            <span className="eyebrow">MVP-demo</span>
            <p>Anonymisoitu testityömaa. Tiedot säilyvät tällä laitteella.</p>
          </div>
        </aside>

        <main className="main-content">
          <header className="topbar">
            <div className="site-identity">
              <span className="eyebrow">Työmaa</span>
              <strong>Satamatie 12</strong>
              <span className="site-code">1048</span>
            </div>
            <div className="top-actions">
              <button className={`sync-status ${isOnline ? "" : "offline"}`} onClick={syncNow}>
                <span className="status-dot" />
                {isOnline ? (data.queue ? `${data.queue} jonossa` : "Synkronoitu") : `${data.queue} offline`}
              </button>
              <label className="role-select">
                <span className="sr-only">Rooli</span>
                <select value={role} onChange={(event) => setRole(event.target.value as typeof role)}>
                  <option>Työntekijä</option>
                  <option>Työnjohtaja</option>
                </select>
              </label>
            </div>
          </header>

          <div className="view-container">
            {view === "home" && (
              <HomeView
                data={data}
                role={role}
                setView={setView}
                syncNow={syncNow}
              />
            )}
            {view === "plan" && (
              <PlanView
                events={data.events}
                onAdd={(event) => {
                  setData((current) => ({
                    ...current,
                    events: [event, ...current.events],
                    queue: current.queue + 1,
                  }));
                  setToast("Tapahtuma tallennettu offline-jonoon.");
                }}
              />
            )}
            {view === "time" && (
              <TimeView
                entries={data.timeEntries}
                settings={data.timeSettings}
                onChange={(entries) => setData((current) => ({ ...current, timeEntries: entries }))}
                onSettingsChange={(settings) =>
                  setData((current) => ({ ...current, timeSettings: settings }))}
                onSave={() => {
                  queueChange();
                  setToast("Tunnit tallennettu ja lähetetty tarkistukseen.");
                }}
                onPrint={() => setPrintMode("timesheet")}
              />
            )}
            {view === "materials" && (
              <MaterialsView
                lots={data.materialLots}
                onChange={(lots) => {
                  setData((current) => ({ ...current, materialLots: lots, queue: current.queue + 1 }));
                  setToast("Materiaalitapahtuma tallennettu.");
                }}
              />
            )}
            {view === "resources" && (
              <ResourcesView
                resources={data.resources}
                role={role}
                onChange={(resources, message) => {
                  setData((current) => ({ ...current, resources, queue: current.queue + 1 }));
                  setToast(message);
                }}
              />
            )}
            {view === "reports" && (
              <ReportsView
                data={data}
                onPrintTimesheet={() => setPrintMode("timesheet")}
                onPrintJournal={() => setPrintMode("journal")}
              />
            )}
          </div>
        </main>

        <nav className="mobile-nav" aria-label="Päänavigaatio">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              className={view === item.id ? "active" : ""}
              onClick={() => setView(item.id)}
            >
              <span aria-hidden="true">{item.short}</span>
              <small>{item.label}</small>
            </button>
          ))}
        </nav>

        {toast && <div className="toast" role="status">{toast}</div>}
      </div>

      <div className="print-zone" aria-hidden={!printMode}>
        {printMode === "timesheet" && (
          <FaithfulTimesheet entries={data.timeEntries} lots={data.materialLots} />
        )}
        {printMode === "journal" && <DailyJournal events={data.events} lots={data.materialLots} />}
      </div>
    </>
  );
}

function HomeView({
  data,
  role,
  setView,
  syncNow,
}: {
  data: LappuData;
  role: string;
  setView: (view: View) => void;
  syncNow: () => void;
}) {
  const hours = data.timeEntries.reduce((sum, entry) => sum + (Number(entry.normalHours) || 0), 0);
  const currentLot = data.materialLots[0];
  const material = currentLot ? calculateMaterialBalance(currentLot.movements) : null;

  return (
    <section className="stack-lg">
      <div className="welcome-row">
        <div>
          <span className="eyebrow">Perjantai 24.7.2026</span>
          <h1>Hyvää työpäivää, Mikko.</h1>
          <p className="lede">Kirjaa tapahtuma lähellä sen syntyhetkeä. Lappu hoitaa jäljen ja yhteenvedot.</p>
        </div>
        <span className="role-badge">{role}</span>
      </div>

      <div className="quick-grid">
        <button className="quick-card primary" onClick={() => setView("plan")}>
          <span className="quick-number">01</span>
          <strong>Merkitse tapahtuma</strong>
          <small>Paikka, kuva ja kuvaus</small>
        </button>
        <button className="quick-card" onClick={() => setView("time")}>
          <span className="quick-number">02</span>
          <strong>Kirjaa tunnit</strong>
          <small>Työnumero 1048-21</small>
        </button>
        <button className="quick-card" onClick={() => setView("materials")}>
          <span className="quick-number">03</span>
          <strong>Hae tussikoodi</strong>
          <small>{currentLot?.code ?? "Luo uusi erä"}</small>
        </button>
        <button className="quick-card" onClick={() => setView("resources")}>
          <span className="quick-number">04</span>
          <strong>Työkalut ja resurssit</strong>
          <small>{data.resources.filter((resource) => resource.status === "Vapaa").length} vapaana työmaalla</small>
        </button>
      </div>

      <div className="content-grid">
        <section className="panel activity-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Tänään työmaalla</span>
              <h2>Tilannekuva</h2>
            </div>
            <button className="text-button" onClick={() => setView("reports")}>Avaa raportit →</button>
          </div>
          <div className="metric-row">
            <div><strong>{hours}</strong><span>kirjattua tuntia</span></div>
            <div><strong>{data.events.length}</strong><span>tapahtumaa</span></div>
            <div><strong>{data.queue}</strong><span>jonossa</span></div>
          </div>
          <div className="timeline">
            {data.events.slice(0, 3).map((event) => (
              <div className="timeline-item" key={event.id}>
                <span className={`event-pin ${event.type === "Vaurio" ? "alert" : ""}`} />
                <div>
                  <strong>{event.type} · {event.floor}</strong>
                  <p>{event.description}</p>
                  <small>{event.room} · {new Date(event.createdAt).toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" })}</small>
                </div>
                <span className="status-chip">{event.status}</span>
              </div>
            ))}
          </div>
        </section>

        <aside className="panel queue-panel">
          <span className="eyebrow">Offline-valmis</span>
          <h2>{data.queue ? `${data.queue} kirjausta odottaa` : "Kaikki ajan tasalla"}</h2>
          <p>Tiedot ja kuvat säilyvät laitteella, kunnes yhteys on käytettävissä.</p>
          <div className="queue-visual">
            <span />
            <span />
            <span className={data.queue ? "waiting" : ""} />
          </div>
          <button className="secondary-button full" onClick={syncNow}>Synkronoi nyt</button>
          {material && (
            <div className="mini-material">
              <span>Varasto A · {currentLot.code}</span>
              <strong>{formatNumber(material.calculated)} {currentLot.unit}</strong>
              {material.difference !== null && <small>Inventaarioero {formatNumber(material.difference)} {currentLot.unit}</small>}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

function PlanView({
  events,
  onAdd,
}: {
  events: SiteEvent[];
  onAdd: (event: SiteEvent) => void;
}) {
  const [floor, setFloor] = useState("2. kerros");
  const [draftPoint, setDraftPoint] = useState<{ x: number; y: number } | null>(null);
  const [eventType, setEventType] = useState<EventType>("Tehty työ");
  const [description, setDescription] = useState("");
  const [billingImpact, setBillingImpact] = useState(false);
  const [photos, setPhotos] = useState<string[]>([]);
  const [overlay, setOverlay] = useState(false);

  const floorEvents = events.filter((event) => event.floor === floor);

  const saveEvent = () => {
    if (!draftPoint || !description.trim()) return;
    onAdd({
      id: crypto.randomUUID(),
      type: eventType,
      description: description.trim(),
      floor,
      room: "Valittu kohta",
      x: draftPoint.x,
      y: draftPoint.y,
      createdAt: new Date().toISOString(),
      billingImpact,
      photos,
      status: "Luonnos",
    });
    setDraftPoint(null);
    setDescription("");
    setBillingImpact(false);
    setPhotos([]);
  };

  return (
    <section className="stack-md plan-section">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Rakennus A · Kohdistettu</span>
          <h1>Pohjakuva ja tapahtumat</h1>
          <p>Napauta pohjakuvaa ja kirjaa havainto oikeaan paikkaan.</p>
        </div>
        <div className="alignment-score">
          <span>RMS-virhe</span>
          <strong>0,8 m</strong>
          <small>hyvä kohdistus</small>
        </div>
      </div>

      <div className="plan-toolbar panel">
        <div className="segmented" role="group" aria-label="Valitse kerros">
          {FLOOR_NAMES.map((name) => (
            <button key={name} className={floor === name ? "active" : ""} onClick={() => setFloor(name)}>
              {name.replace(". kerros", ". krs")}
            </button>
          ))}
        </div>
        <label className="toggle">
          <input type="checkbox" checked={overlay} onChange={(event) => setOverlay(event.target.checked)} />
          <span />
          Päällekkäisnäkymä
        </label>
        <span className="plan-version">Suunnitelma v3 · 14.7.2026</span>
      </div>

      <div className="plan-workspace">
        <div className="floor-plan-card">
          <div
            className="floor-plan"
            role="button"
            tabIndex={0}
            aria-label="Pohjakuva. Valitse merkinnän sijainti."
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setDraftPoint({
                x: Math.round(((event.clientX - rect.left) / rect.width) * 100),
                y: Math.round(((event.clientY - rect.top) / rect.height) * 100),
              });
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") setDraftPoint({ x: 50, y: 50 });
            }}
          >
            <PlanDrawing overlay={overlay} />
            {floorEvents.map((siteEvent, index) => (
              <span
                key={siteEvent.id}
                className={`map-marker ${siteEvent.type === "Vaurio" ? "alert" : ""}`}
                style={{ left: `${siteEvent.x}%`, top: `${siteEvent.y}%` }}
                title={`${siteEvent.type}: ${siteEvent.description}`}
              >
                {index + 1}
              </span>
            ))}
            {draftPoint && (
              <span className="map-marker draft" style={{ left: `${draftPoint.x}%`, top: `${draftPoint.y}%` }}>+</span>
            )}
            <div className="plan-scale">0&nbsp;&nbsp;&nbsp;5&nbsp;&nbsp;&nbsp;10 m</div>
            <div className="north-arrow">N ↑</div>
          </div>
          <div className="plan-footer">
            <span>{floor} · {floorEvents.length} tapahtumaa</span>
            <span>Valitse sijainti pohjakuvasta</span>
          </div>
        </div>

        <aside className={`event-drawer panel ${draftPoint ? "open" : ""}`}>
          {!draftPoint ? (
            <div className="empty-state">
              <span className="empty-plus">+</span>
              <h2>Uusi tapahtuma</h2>
              <p>Valitse ensin sijainti pohjakuvasta.</p>
              <div className="event-list-compact">
                {floorEvents.map((event) => (
                  <div key={event.id}>
                    <span className={`event-pin ${event.type === "Vaurio" ? "alert" : ""}`} />
                    <span><strong>{event.type}</strong><small>{event.description}</small></span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="event-form">
              <div className="drawer-heading">
                <div>
                  <span className="eyebrow">{floor} · kohta {draftPoint.x}/{draftPoint.y}</span>
                  <h2>Kirjaa tapahtuma</h2>
                </div>
                <button className="icon-button" onClick={() => setDraftPoint(null)} aria-label="Sulje">×</button>
              </div>

              <fieldset className="choice-grid">
                <legend>Tapahtumatyyppi</legend>
                {(["Tehty työ", "Vaurio", "Puute", "Muistiinpano"] as EventType[]).map((type) => (
                  <label key={type} className={eventType === type ? "selected" : ""}>
                    <input
                      type="radio"
                      name="event-type"
                      value={type}
                      checked={eventType === type}
                      onChange={() => setEventType(type)}
                    />
                    {type}
                  </label>
                ))}
              </fieldset>

              <label className="field">
                <span>Kuvaus</span>
                <textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Mitä tapahtui tai havaittiin?"
                  rows={4}
                />
              </label>

              <label className="upload-button">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => setPhotos(Array.from(event.target.files ?? []).map((file) => file.name))}
                />
                <span>＋</span> Lisää kuvat
                {photos.length > 0 && <small>{photos.length} valittu</small>}
              </label>

              <label className="check-row">
                <input type="checkbox" checked={billingImpact} onChange={(event) => setBillingImpact(event.target.checked)} />
                <span>
                  <strong>Vaikuttaa laskutukseen</strong>
                  <small>Merkitään työnjohdon tarkistettavaksi</small>
                </span>
              </label>

              <button className="primary-button full" disabled={!description.trim()} onClick={saveEvent}>
                Tallenna luonnos
              </button>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

function PlanDrawing({ overlay }: { overlay: boolean }) {
  return (
    <svg viewBox="0 0 900 580" className="plan-svg" aria-hidden="true">
      <rect x="38" y="42" width="824" height="490" rx="2" fill="#faf9f4" stroke="#758079" strokeWidth="7" />
      <path d="M290 45v487M590 45v487M40 300h820M290 170h300M430 170v130M720 45v255" fill="none" stroke="#89918b" strokeWidth="4" />
      <path d="M82 96h150v130H82zM330 350h210v130H330zM640 350h170v130H640z" fill="none" stroke="#a2aaa4" strokeWidth="3" />
      <path d="M54 315h220M606 315h238M305 55v95M305 318v205" stroke="#faf9f4" strokeWidth="12" />
      <path d="M54 315h220M606 315h238M305 55v95M305 318v205" stroke="#8e9791" strokeWidth="2" strokeDasharray="7 6" />
      <text x="128" y="165">1.01 VARASTO</text>
      <text x="375" y="235">1.04 KÄYTÄVÄ</text>
      <text x="657" y="165">1.08 TYÖTILA</text>
      <text x="382" y="422">1.12 SÄHKÖ</text>
      <text x="678" y="422">1.14 TEKN.</text>
      <path d="M415 45v125M440 45v125M465 45v125M490 45v125M515 45v125" stroke="#b1b7b2" strokeWidth="2" />
      {overlay && (
        <g className="overlay-plan">
          <rect x="52" y="56" width="796" height="462" fill="none" stroke="#d7784d" strokeWidth="4" />
          <path d="M302 58v460M52 310h796M600 58v460" fill="none" stroke="#d7784d" strokeWidth="3" />
        </g>
      )}
    </svg>
  );
}

function TimeView({
  entries,
  settings,
  onChange,
  onSettingsChange,
  onSave,
  onPrint,
}: {
  entries: TimeEntry[];
  settings: TimeSettings;
  onChange: (entries: TimeEntry[]) => void;
  onSettingsChange: (settings: TimeSettings) => void;
  onSave: () => void;
  onPrint: () => void;
}) {
  const [activeDate, setActiveDate] = useState(
    entries.find((entry) => !entry.submitted)?.date ?? entries.at(-1)?.date ?? DAYS[0],
  );
  const [showSettings, setShowSettings] = useState(false);
  const total = entries.reduce((sum, entry) => sum + entryHourTotal(entry), 0);
  const dayEntries = entries
    .filter((entry) => entry.date === activeDate)
    .sort((a, b) => a.startMinutes - b.startMinutes);

  const update = (id: string, field: keyof TimeEntry, value: string | boolean) => {
    onChange(entries.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)));
  };
  const hourFields: Array<{ field: keyof TimeEntry; label: string }> = [
    { field: "normalHours", label: "Norm" },
    { field: "ur", label: "UR" },
    { field: "hal", label: "HÄL" },
    { field: "overtime50", label: "50%" },
    { field: "overtime100", label: "100%" },
    { field: "su", label: "SU" },
    { field: "pvr", label: "PVR" },
    { field: "rr", label: "rr" },
    { field: "kilometres", label: "km" },
  ];

  return (
    <section className="stack-md">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Viikko 30 · 20.–24.7.2026</span>
          <h1>Omat tunnit</h1>
          <p>Tyhjä arvo säilyy tyhjänä. Hyväksyttyä aineistoa ei ylikirjoiteta.</p>
        </div>
        <div className="time-heading-actions">
          <button className="secondary-button" type="button" onClick={() => setShowSettings((value) => !value)}>
            {showSettings ? "Sulje asetukset" : "Työaika-asetukset"}
          </button>
          <div className="total-badge">
            <strong>{formatNumber(total)} h</strong>
            <span>viikon yhteensä</span>
          </div>
        </div>
      </div>

      {showSettings && (
        <section className="panel time-settings" aria-labelledby="time-settings-heading">
          <div>
            <span className="eyebrow">Uuden päivän oletus</span>
            <h2 id="time-settings-heading">Työpäivän asetukset</h2>
            <p>Asetus muodostaa uuden päivän yhdeksi blokiksi. Voit soveltaa sitä myös tämän viikon luonnospäiviin.</p>
          </div>
          <label className="field">
            <span>Päivä alkaa</span>
            <input
              type="time"
              step={settings.snapMinutes * 60}
              value={formatClock(settings.dayStartMinutes)}
              onChange={(event) =>
                onSettingsChange({ ...settings, dayStartMinutes: parseClock(event.target.value) })}
            />
          </label>
          <label className="field">
            <span>Oletuspituus (h)</span>
            <input
              type="number"
              inputMode="decimal"
              min="0.25"
              max="16"
              step="0.25"
              value={settings.defaultDailyHours}
              onChange={(event) =>
                onSettingsChange({
                  ...settings,
                  defaultDailyHours: Math.max(0.25, Math.min(16, Number(event.target.value) || 0.25)),
                })}
            />
          </label>
          <button
            className="primary-button"
            type="button"
            onClick={() => onChange(applySettingsToDraftDays(entries, settings))}
          >
            Käytä luonnospäiviin
          </button>
        </section>
      )}

      <div className="panel time-panel timeline-panel">
        <TimeTimeline
          entries={entries}
          activeDate={activeDate}
          onActiveDate={setActiveDate}
          onChange={onChange}
          activeDetails={(
            <section className="time-block-details" aria-labelledby="time-block-details-heading">
          <div className="block-details-heading">
            <div>
              <span className="eyebrow">Alkuperäisen tuntilapun kentät</span>
              <h2 id="time-block-details-heading">
                {new Date(`${activeDate}T12:00:00`).toLocaleDateString("fi-FI", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </h2>
            </div>
            <span>{dayEntries.length} {dayEntries.length === 1 ? "työblokki" : "työblokkia"}</span>
          </div>

          <div className="time-block-list">
            {dayEntries.map((entry, index) => (
              <article className="time-block-editor" key={entry.id}>
                <header>
                  <div className={`block-number block-tone-${(index % 3) + 1}`}>{index + 1}</div>
                  <div>
                    <strong>{formatClock(entry.startMinutes)}–{formatClock(entry.endMinutes)}</strong>
                    <span>{durationLabel(entry)}</span>
                  </div>
                  <span className={entry.submitted ? "status-chip" : "status-chip muted"}>
                    {entry.submitted ? "Lähetetty" : "Luonnos"}
                  </span>
                  <button
                    className="split-button"
                    type="button"
                    disabled={durationHours(entry) < 0.5}
                    onClick={() =>
                      onChange(splitTimeEntry(entries, entry.id, `time-${crypto.randomUUID()}`))}
                  >
                    Jaa blokki kahtia
                  </button>
                </header>
                <div className="block-main-fields">
                  <label className="field">
                    <span>Työnumero</span>
                    <input
                      value={entry.workNumber}
                      onChange={(event) => update(entry.id, "workNumber", event.target.value)}
                    />
                  </label>
                  <label className="field block-description">
                    <span>Työnselitys</span>
                    <textarea
                      rows={2}
                      value={entry.description}
                      onChange={(event) => update(entry.id, "description", event.target.value)}
                      placeholder="Mitä teit tämän blokin aikana?"
                    />
                  </label>
                </div>
                <div className="paper-hour-fields">
                  {hourFields.map(({ field, label }) => (
                    <label className="field" key={field}>
                      <span>{label}</span>
                      <input
                        inputMode="decimal"
                        value={String(entry[field])}
                        onChange={(event) => update(entry.id, field, event.target.value)}
                        placeholder="—"
                      />
                    </label>
                  ))}
                </div>
              </article>
            ))}
          </div>
            </section>
          )}
        />

        <div className="time-actions">
          <div>
            <strong>{formatNumber(dayEntries.reduce((sum, entry) => sum + entryHourTotal(entry), 0))} h</strong>
            <span>valitun päivän tuntilappusarakkeissa</span>
          </div>
          <button className="secondary-button" onClick={onPrint}>Esikatsele A4</button>
          <button
            className="primary-button"
            onClick={() => {
              onChange(entries.map((entry) => ({ ...entry, submitted: true })));
              onSave();
            }}
          >
            Lähetä tarkistukseen
          </button>
        </div>
      </div>
    </section>
  );
}

function MaterialsView({
  lots,
  onChange,
}: {
  lots: MaterialLot[];
  onChange: (lots: MaterialLot[]) => void;
}) {
  const [selectedId, setSelectedId] = useState(lots[0]?.id ?? "");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [movementType, setMovementType] = useState<MaterialMovementType>("received");
  const [movementAmount, setMovementAmount] = useState("");
  const selected = lots.find((lot) => lot.id === selectedId) ?? lots[0];
  const balance = selected ? calculateMaterialBalance(selected.movements) : null;

  const createLot = () => {
    if (!newName.trim()) return;
    const lot: MaterialLot = {
      id: crypto.randomUUID(),
      code: createMarkerCode(),
      name: newName.trim(),
      unit: "kpl",
      location: "1. krs · Varasto A",
      movements: [],
    };
    onChange([lot, ...lots]);
    setSelectedId(lot.id);
    setNewName("");
    setShowCreate(false);
  };

  const addMovement = () => {
    if (!selected || !movementAmount || Number.isNaN(Number(movementAmount))) return;
    const movement: MaterialMovement = {
      id: crypto.randomUUID(),
      type: movementType,
      amount: Number(movementAmount),
      at: new Date().toISOString(),
      location: selected.location,
    };
    onChange(lots.map((lot) => (lot.id === selected.id ? { ...lot, movements: [...lot.movements, movement] } : lot)));
    setMovementAmount("");
  };

  return (
    <section className="stack-md">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Työmaan materiaalit</span>
          <h1>Materiaalierät</h1>
          <p>Saldo johdetaan tapahtumista. Inventointi säilyttää aikaisemman historian.</p>
        </div>
        <button className="primary-button" onClick={() => setShowCreate(true)}>＋ Uusi materiaalierä</button>
      </div>

      {showCreate && (
        <div className="panel create-lot">
          <div>
            <span className="eyebrow">Toimii myös offline</span>
            <h2>Luo tussikoodillinen erä</h2>
          </div>
          <label className="field">
            <span>Materiaalin nimi tai väliaikainen kuvaus</span>
            <input autoFocus value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="Esim. MMJ 3×2,5 laatikko" />
          </label>
          <div className="inline-actions">
            <button className="text-button" onClick={() => setShowCreate(false)}>Peruuta</button>
            <button className="primary-button" disabled={!newName.trim()} onClick={createLot}>Generoi koodi</button>
          </div>
        </div>
      )}

      <div className="materials-layout">
        <aside className="panel lot-list">
          <label className="search-field">
            <span className="sr-only">Hae tussikoodilla tai nimellä</span>
            <input placeholder="Hae koodilla tai nimellä…" />
          </label>
          {lots.map((lot) => {
            const lotBalance = calculateMaterialBalance(lot.movements);
            return (
              <button key={lot.id} className={selected?.id === lot.id ? "lot-row active" : "lot-row"} onClick={() => setSelectedId(lot.id)}>
                <span className="marker-code small">{lot.code}</span>
                <span><strong>{lot.name}</strong><small>{lot.location}</small></span>
                <strong>{formatNumber(lotBalance.calculated)} {lot.unit}</strong>
              </button>
            );
          })}
        </aside>

        {selected && balance ? (
          <section className="panel material-detail">
            <div className="material-heading">
              <div>
                <span className="marker-code">{selected.code}</span>
                <h2>{selected.name}</h2>
                <p>{selected.location}</p>
              </div>
              <button className="secondary-button" onClick={() => navigator.clipboard?.writeText(selected.code)}>Kopioi koodi</button>
            </div>
            <div className="balance-grid">
              <div><span>Laskennallinen</span><strong>{formatNumber(balance.calculated)} {selected.unit}</strong></div>
              <div><span>Viimeisin inventointi</span><strong>{formatNumber(balance.inventoried)} {selected.unit}</strong></div>
              <div className={balance.difference && balance.difference !== 0 ? "difference" : ""}>
                <span>Inventaarioero</span><strong>{formatNumber(balance.difference)} {selected.unit}</strong>
              </div>
            </div>

            <div className="movement-form">
              <label className="field">
                <span>Tapahtuma</span>
                <select value={movementType} onChange={(event) => setMovementType(event.target.value as MaterialMovementType)}>
                  <option value="received">Vastaanotettu</option>
                  <option value="transferred">Siirretty</option>
                  <option value="used">Käytetty</option>
                  <option value="inventoried">Inventoitu</option>
                </select>
              </label>
              <label className="field">
                <span>Määrä ({selected.unit})</span>
                <input inputMode="decimal" value={movementAmount} onChange={(event) => setMovementAmount(event.target.value)} placeholder="0" />
              </label>
              <button className="primary-button" onClick={addMovement}>Tallenna tapahtuma</button>
            </div>

            <div className="movement-history">
              <h3>Tapahtumahistoria</h3>
              {[...selected.movements].reverse().map((movement) => (
                <div key={movement.id}>
                  <span className="movement-dot" />
                  <span>
                    <strong>{movementLabel(movement.type)}</strong>
                    <small>{new Date(movement.at).toLocaleString("fi-FI", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}</small>
                  </span>
                  <strong>{movement.type === "used" ? "−" : movement.type === "received" ? "+" : ""}{formatNumber(movement.amount)} {selected.unit}</strong>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <section className="panel empty-state"><h2>Ei materiaalieriä</h2><p>Luo ensimmäinen erä aloittaaksesi.</p></section>
        )}
      </div>
    </section>
  );
}

function movementLabel(type: MaterialMovementType) {
  return {
    received: "Vastaanotettu",
    transferred: "Siirretty",
    used: "Käytetty",
    inventoried: "Inventoitu",
  }[type];
}

function ResourcesView({
  resources,
  role,
  onChange,
}: {
  resources: ResourceItem[];
  role: "Työntekijä" | "Työnjohtaja";
  onChange: (resources: ResourceItem[], message: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"Kaikki" | ResourceStatus>("Kaikki");
  const [selectedId, setSelectedId] = useState(resources[0]?.id ?? "");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState<ResourceCategory>("Työkalu");
  const [newLocation, setNewLocation] = useState("1. krs · Työkaluvarasto");
  const [newAssignedTo, setNewAssignedTo] = useState("");
  const [bindingLocation, setBindingLocation] = useState(resources[0]?.location ?? RESOURCE_LOCATIONS[0]);
  const [bindingEmployee, setBindingEmployee] = useState(resources[0]?.assignedTo ?? "");

  const filteredResources = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("fi-FI");
    return resources.filter((resource) => {
      const matchesStatus = statusFilter === "Kaikki" || resource.status === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        [resource.code, resource.name, resource.category, resource.location, resource.assignedTo ?? ""]
          .some((value) => value.toLocaleLowerCase("fi-FI").includes(normalizedQuery));
      return matchesStatus && matchesQuery;
    });
  }, [query, resources, statusFilter]);

  const selected =
    resources.find((resource) => resource.id === selectedId) ??
    filteredResources[0] ??
    resources[0];

  useEffect(() => {
    if (!selected) return;
    setBindingLocation(selected.location);
    setBindingEmployee(selected.assignedTo ?? "");
  }, [selected?.id, selected?.location, selected?.assignedTo]);

  const updateResource = (
    id: string,
    changes: Partial<ResourceItem>,
    message: string,
  ) => {
    onChange(
      resources.map((resource) => (resource.id === id ? { ...resource, ...changes } : resource)),
      message,
    );
  };

  const createResource = () => {
    if (!newName.trim()) return;
    const prefixes: Record<ResourceCategory, string> = {
      Työkalu: "TY",
      Kone: "KO",
      Mittalaite: "MI",
      Ajoneuvo: "AJ",
    };
    const usedCodes = new Set(resources.map((resource) => resource.code));
    let code = "";
    do {
      code = `${prefixes[newCategory]}-${Math.floor(1 + Math.random() * 998).toString().padStart(3, "0")}`;
    } while (usedCodes.has(code));
    const resource: ResourceItem = {
      id: crypto.randomUUID(),
      code,
      name: newName.trim(),
      category: newCategory,
      status: newAssignedTo ? "Käytössä" : "Vapaa",
      location: newLocation.trim() || "1. krs · Työkaluvarasto",
      assignedTo: newAssignedTo || undefined,
    };
    onChange([resource, ...resources], `${resource.code} lisätty resurssirekisteriin.`);
    setSelectedId(resource.id);
    setNewName("");
    setNewCategory("Työkalu");
    setNewLocation("1. krs · Työkaluvarasto");
    setNewAssignedTo("");
    setQuery("");
    setStatusFilter("Kaikki");
    setShowCreate(false);
  };

  const counts = {
    all: resources.length,
    available: resources.filter((resource) => resource.status === "Vapaa").length,
    inUse: resources.filter((resource) => resource.status === "Käytössä").length,
    service: resources.filter((resource) => resource.status === "Huollossa").length,
  };

  return (
    <section className="stack-md resources-section">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Työmaan yhteinen kalusto</span>
          <h1>Työkalu- ja resurssirekisteri</h1>
          <p>Näe vapaa kalusto, vastuuhenkilö ja viimeisin sijainti myös ilman verkkoyhteyttä.</p>
        </div>
        <button className="primary-button" onClick={() => setShowCreate(true)}>+ Lisää resurssi</button>
      </div>

      <div className="resource-metrics" aria-label="Resurssien tilanne">
        <button className={statusFilter === "Kaikki" ? "active" : ""} onClick={() => setStatusFilter("Kaikki")}>
          <span>Kaikki</span><strong>{counts.all}</strong>
        </button>
        <button className={statusFilter === "Vapaa" ? "active" : ""} onClick={() => setStatusFilter("Vapaa")}>
          <span className="resource-dot available" />Vapaana<strong>{counts.available}</strong>
        </button>
        <button className={statusFilter === "Käytössä" ? "active" : ""} onClick={() => setStatusFilter("Käytössä")}>
          <span className="resource-dot in-use" />Käytössä<strong>{counts.inUse}</strong>
        </button>
        <button className={statusFilter === "Huollossa" ? "active" : ""} onClick={() => setStatusFilter("Huollossa")}>
          <span className="resource-dot service" />Huollossa<strong>{counts.service}</strong>
        </button>
      </div>

      {showCreate && (
        <div className="panel resource-create">
          <div className="resource-create-heading">
            <div>
              <span className="eyebrow">Uusi rekisterimerkintä</span>
              <h2>Lisää työmaan resurssi</h2>
            </div>
            <button className="icon-button" onClick={() => setShowCreate(false)} aria-label="Sulje">×</button>
          </div>
          <label className="field">
            <span>Nimi</span>
            <input autoFocus value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="Esim. Akkukulmahiomakone 18 V" />
          </label>
          <label className="field">
            <span>Luokka</span>
            <select value={newCategory} onChange={(event) => setNewCategory(event.target.value as ResourceCategory)}>
              <option>Työkalu</option>
              <option>Kone</option>
              <option>Mittalaite</option>
              <option>Ajoneuvo</option>
            </select>
          </label>
          <label className="field">
            <span>Sijainti</span>
            <select value={newLocation} onChange={(event) => setNewLocation(event.target.value)}>
              {RESOURCE_LOCATIONS.map((location) => <option key={location}>{location}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Työntekijä</span>
            <select value={newAssignedTo} onChange={(event) => setNewAssignedTo(event.target.value)}>
              <option value="">Ei työntekijäsidontaa</option>
              {WORKERS.map((worker) => <option key={worker}>{worker}</option>)}
            </select>
          </label>
          <div className="inline-actions">
            <button className="text-button" onClick={() => setShowCreate(false)}>Peruuta</button>
            <button className="primary-button" disabled={!newName.trim()} onClick={createResource}>Lisää rekisteriin</button>
          </div>
        </div>
      )}

      <div className="resources-layout">
        <aside className="panel resource-list">
          <label className="search-field">
            <span className="sr-only">Hae resurssia</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Hae nimellä, koodilla tai sijainnilla…" />
          </label>
          <div className="resource-list-meta">
            <span>{filteredResources.length} resurssia</span>
            {statusFilter !== "Kaikki" && <button className="text-button" onClick={() => setStatusFilter("Kaikki")}>Tyhjennä rajaus</button>}
          </div>
          {filteredResources.map((resource) => (
            <button
              key={resource.id}
              className={selected?.id === resource.id ? "resource-row active" : "resource-row"}
              onClick={() => setSelectedId(resource.id)}
            >
              <span className={`resource-symbol ${resource.category.toLocaleLowerCase("fi-FI")}`}>{resourceCategoryShort(resource.category)}</span>
              <span>
                <strong>{resource.name}</strong>
                <small>{resource.code} · {resource.location}</small>
              </span>
              <span className={`resource-status ${resourceStatusClass(resource.status)}`}>{resource.status}</span>
            </button>
          ))}
          {filteredResources.length === 0 && (
            <div className="resource-empty"><strong>Ei hakutuloksia</strong><span>Kokeile toista hakusanaa tai tilaa.</span></div>
          )}
        </aside>

        {selected ? (
          <section className="panel resource-detail">
            <div className="resource-detail-heading">
              <div className={`resource-symbol large ${selected.category.toLocaleLowerCase("fi-FI")}`}>{resourceCategoryShort(selected.category)}</div>
              <div>
                <span className="eyebrow">{selected.category} · {selected.code}</span>
                <h2>{selected.name}</h2>
                <span className={`resource-status ${resourceStatusClass(selected.status)}`}>{selected.status}</span>
              </div>
            </div>

            <dl className="resource-facts">
              <div><dt>Sidottu paikkaan</dt><dd>{selected.location}</dd></div>
              <div><dt>Sidottu työntekijään</dt><dd>{selected.assignedTo ?? "Ei työntekijäsidontaa"}</dd></div>
              <div>
                <dt>Seuraava tarkistus</dt>
                <dd>{selected.inspectionDue ? new Date(`${selected.inspectionDue}T12:00:00`).toLocaleDateString("fi-FI") : "Ei määritetty"}</dd>
              </div>
              <div><dt>Lisätiedot</dt><dd>{selected.notes ?? "Ei lisätietoja"}</dd></div>
            </dl>

            <section className="resource-binding" aria-labelledby="resource-binding-title">
              <div className="resource-binding-heading">
                <div>
                  <span className="eyebrow">Vastuu ja paikannus</span>
                  <h3 id="resource-binding-title">Sido paikkaan ja työntekijään</h3>
                </div>
                {selected.status === "Huollossa" && <span className="resource-status service">Huollossa</span>}
              </div>
              <label className="field">
                <span>Paikka</span>
                <select value={bindingLocation} onChange={(event) => setBindingLocation(event.target.value)}>
                  {[...new Set([bindingLocation, ...RESOURCE_LOCATIONS])].map((location) => (
                    <option key={location}>{location}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Työntekijä</span>
                <select
                  value={bindingEmployee}
                  disabled={selected.status === "Huollossa"}
                  onChange={(event) => setBindingEmployee(event.target.value)}
                >
                  <option value="">Ei työntekijäsidontaa</option>
                  {WORKERS.map((worker) => <option key={worker}>{worker}</option>)}
                </select>
              </label>
              <button
                className="primary-button"
                onClick={() => {
                  const employee = selected.status === "Huollossa" ? undefined : bindingEmployee || undefined;
                  updateResource(
                    selected.id,
                    {
                      location: bindingLocation,
                      assignedTo: employee,
                      status: selected.status === "Huollossa" ? "Huollossa" : employee ? "Käytössä" : "Vapaa",
                    },
                    employee
                      ? `${selected.code} sidottu paikkaan ja työntekijään.`
                      : `${selected.code} sidottu paikkaan.`,
                  );
                }}
              >
                Tallenna sidonta
              </button>
            </section>

            <div className="resource-actions">
              {selected.status === "Vapaa" && (
                <button
                  className="primary-button"
                  onClick={() => updateResource(
                    selected.id,
                    { status: "Käytössä", assignedTo: "Mikko Mallikas", location: bindingLocation },
                    `${selected.code} otettu käyttöön.`,
                  )}
                >
                  Ota itse käyttöön
                </button>
              )}
              {selected.status === "Käytössä" && (
                <button
                  className="primary-button"
                  onClick={() => updateResource(
                    selected.id,
                    { status: "Vapaa", assignedTo: undefined, location: bindingLocation },
                    `${selected.code} palautettu varastoon.`,
                  )}
                >
                  Vapauta työntekijältä
                </button>
              )}
              {selected.status === "Huollossa" && role === "Työnjohtaja" && (
                <button
                  className="primary-button"
                  onClick={() => updateResource(
                    selected.id,
                    { status: "Vapaa", location: "1. krs · Työkaluvarasto" },
                    `${selected.code} merkitty käyttövalmiiksi.`,
                  )}
                >
                  Merkitse käyttövalmiiksi
                </button>
              )}
              {selected.status !== "Huollossa" && (
                <button
                  className="secondary-button"
                  onClick={() => updateResource(
                    selected.id,
                    { status: "Huollossa", assignedTo: undefined, location: "Piha · Huoltoalue" },
                    `${selected.code} siirretty huoltoon.`,
                  )}
                >
                  Siirrä huoltoon
                </button>
              )}
            </div>
            <p className="resource-role-note">
              {role === "Työnjohtaja"
                ? "Työnjohtajana voit vapauttaa huollossa olevan kaluston takaisin käyttöön."
                : "Paikka- ja työntekijäsidonnat sekä tilamuutokset kirjautuvat offline-jonoon."}
            </p>
          </section>
        ) : (
          <section className="panel empty-state"><h2>Ei resursseja</h2><p>Lisää ensimmäinen työkalu tai kone rekisteriin.</p></section>
        )}
      </div>
    </section>
  );
}

function resourceCategoryShort(category: ResourceCategory) {
  return {
    Työkalu: "TY",
    Kone: "KO",
    Mittalaite: "MI",
    Ajoneuvo: "AJ",
  }[category];
}

function resourceStatusClass(status: ResourceStatus) {
  return {
    Vapaa: "available",
    Käytössä: "in-use",
    Huollossa: "service",
  }[status];
}

function ReportsView({
  data,
  onPrintTimesheet,
  onPrintJournal,
}: {
  data: LappuData;
  onPrintTimesheet: () => void;
  onPrintJournal: () => void;
}) {
  const reportCards = [
    {
      title: "Alkuperäinen tuntilappu",
      description: "Uskollinen A4-tuloste, 8 työaikariviä ja 11 materiaaliriviä.",
      meta: "Viikko 30 · Mikko Mallikas",
      action: onPrintTimesheet,
      label: "Tulosta / tallenna PDF",
    },
    {
      title: "Työmaapäiväkirja",
      description: "Päivän tapahtumat, sijainnit, kuvatiedot ja materiaalit yhdessä.",
      meta: `24.7.2026 · ${data.events.length} tapahtumaa`,
      action: onPrintJournal,
      label: "Muodosta päiväkirja",
    },
  ];

  return (
    <section className="stack-md">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Johdetut aineistot</span>
          <h1>Raportit ja tulosteet</h1>
          <p>Sama kirjattu tieto muuntuu tutuksi paperiksi ja työmaan tilannekuvaksi.</p>
        </div>
      </div>
      <div className="report-grid">
        {reportCards.map((card, index) => (
          <article className="panel report-card" key={card.title}>
            <div className={`document-preview preview-${index + 1}`}>
              <span className="preview-brand">LAPPU</span>
              <span />
              <span />
              <span />
              <span />
            </div>
            <div>
              <span className="eyebrow">{card.meta}</span>
              <h2>{card.title}</h2>
              <p>{card.description}</p>
              <button className="primary-button" onClick={card.action}>{card.label}</button>
            </div>
          </article>
        ))}
      </div>
      <div className="panel prototype-boundary">
        <span className="eyebrow">Prototyypin rajaus</span>
        <p>
          Tulostus avaa selaimen tulostusnäkymän, josta raportin voi tallentaa PDF-muotoon.
          Jaettu tietokanta, aidot käyttäjäoikeudet ja alkuperäiset suunnitelma-PDF:t kuuluvat seuraavaan pilotointivaiheeseen.
        </p>
      </div>
    </section>
  );
}

function FaithfulTimesheet({ entries, lots }: { entries: TimeEntry[]; lots: MaterialLot[] }) {
  const rows = [...entries, ...Array.from({ length: Math.max(0, 8 - entries.length) }, (_, index) => ({
    id: `empty-${index}`,
    date: "",
    startMinutes: 0,
    endMinutes: 0,
    workNumber: "",
    description: "",
    normalHours: "",
    ur: "",
    hal: "",
    overtime50: "",
    overtime100: "",
    su: "",
    pvr: "",
    rr: "",
    kilometres: "",
    submitted: false,
  }))].slice(0, 8);
  const materialRows = [
    ...lots.map((lot) => ({
      code: lot.code,
      name: lot.name,
      quantity: formatNumber(calculateMaterialBalance(lot.movements).calculated),
      unit: lot.unit,
    })),
    ...Array.from({ length: 11 }, () => ({ code: "", name: "", quantity: "", unit: "" })),
  ].slice(0, 11);

  return (
    <article className="timesheet-print print-document">
      <header>
        <div className="paper-logo">LAPPU</div>
        <div className="paper-title">TYÖILMOITUS</div>
      </header>
      <table className="info-table"><tbody>
        <tr><th>Työntekijä</th><td>Mikko Mallikas</td><th>Asiakas</th><td>Anonymisoitu Oy</td><th>Osoite</th><td>Satamatie 12</td></tr>
        <tr><th>Työnumero</th><td>1048-21</td><th>Tilaaja</th><td>Demoasiakas</td><th></th><td>00180 Helsinki</td></tr>
        <tr><th>Tilaajan nro</th><td>DEM-24</td><th>Kohde</th><td>Rakennus A</td><th></th><td></td></tr>
      </tbody></table>
      <table className="paper-time-table">
        <colgroup>
          <col style={{ width: "18mm" }} />
          <col style={{ width: "100mm" }} />
          <col style={{ width: "8mm" }} />
          <col style={{ width: "7mm" }} />
          <col style={{ width: "8mm" }} />
          <col style={{ width: "8mm" }} />
          <col style={{ width: "9mm" }} />
          <col style={{ width: "8mm" }} />
          <col style={{ width: "8mm" }} />
          <col style={{ width: "8mm" }} />
          <col style={{ width: "8mm" }} />
        </colgroup>
        <thead>
        <tr className="group-row"><th colSpan={2}></th><th colSpan={3}></th><th colSpan={3}>vrk/vko</th><th colSpan={3}>Matkakorvaus</th></tr>
        <tr><th>Pvm</th><th>Työnselvitys</th><th>Norm</th><th>UR</th><th>HÄL</th><th>50%</th><th>100%</th><th>SU</th><th>PVR</th><th>rr</th><th>km</th></tr>
      </thead><tbody>
        {rows.map((entry) => (
          <tr key={entry.id}>
            <td>{entry.date ? new Date(`${entry.date}T12:00:00`).toLocaleDateString("fi-FI", { day: "numeric", month: "numeric" }) : ""}</td>
            <td>{entry.description}</td>
            <td>{entry.normalHours}</td><td>{entry.ur}</td><td>{entry.hal}</td><td>{entry.overtime50}</td><td>{entry.overtime100}</td><td>{entry.su}</td><td>{entry.pvr}</td><td>{entry.rr}</td><td>{entry.kilometres}</td>
          </tr>
        ))}
      </tbody></table>
      <h2>MATERIAALIT</h2>
      <table className="paper-material-table"><thead><tr><th>Koodi</th><th>Nimike</th><th>Määrä</th><th>Yksikkö</th><th>Yksikköhinta</th><th>Yhteensä</th></tr></thead><tbody>
        {materialRows.map((row, index) => (
          <tr key={`${row.code}-${index}`}><td>{row.code}</td><td>{row.name}</td><td>{row.quantity}</td><td>{row.unit}</td><td></td><td></td></tr>
        ))}
      </tbody></table>
      <section className="paper-notes"><strong>Muuta</strong><div /></section>
      <footer><div><span>Tilaajan kuittaus</span><i /></div><div><span>Työnjohdon kuittaus</span><i /></div></footer>
    </article>
  );
}

function DailyJournal({ events, lots }: { events: SiteEvent[]; lots: MaterialLot[] }) {
  return (
    <article className="journal-print print-document">
      <header>
        <div><span className="paper-logo">LAPPU</span><small>TYÖMAAPÄIVÄKIRJA</small></div>
        <div><strong>Satamatie 12</strong><span>24.7.2026 · Rakennus A</span></div>
      </header>
      <section className="journal-summary">
        <div><strong>{events.length}</strong><span>tapahtumaa</span></div>
        <div><strong>{events.filter((event) => event.billingImpact).length}</strong><span>laskutusvaikutusta</span></div>
        <div><strong>{lots.length}</strong><span>materiaalierää</span></div>
      </section>
      <h1>Päivän tapahtumat</h1>
      {events.map((event, index) => (
        <section className="journal-event" key={event.id}>
          <div className="journal-number">{String(index + 1).padStart(2, "0")}</div>
          <div>
            <span>{event.type} · {event.floor} · {event.room}</span>
            <h2>{event.description}</h2>
            <p>{event.photos.length ? `${event.photos.length} kuvaa liitetty` : "Ei kuvia"} · Tila: {event.status}</p>
          </div>
          {event.billingImpact && <strong className="billing-flag">LASKUTUS</strong>}
        </section>
      ))}
      <h1>Materiaalitilanne</h1>
      <table><thead><tr><th>Tussikoodi</th><th>Materiaali</th><th>Sijainti</th><th>Laskennallinen saldo</th><th>Inventaarioero</th></tr></thead><tbody>
        {lots.map((lot) => {
          const balance = calculateMaterialBalance(lot.movements);
          return <tr key={lot.id}><td>{lot.code}</td><td>{lot.name}</td><td>{lot.location}</td><td>{formatNumber(balance.calculated)} {lot.unit}</td><td>{formatNumber(balance.difference)} {lot.unit}</td></tr>;
        })}
      </tbody></table>
      <footer>Muodostettu Lappu MVP -demosta · Anonymisoitu testiaineisto</footer>
    </article>
  );
}

export default App;
